<template>
  <div class="sender-page">
    <h2>{{ $t('W2P_LETTER_SENDER') }}</h2>
    <p class="small">{{ $t('W2P_LETTER_SENDER_SUBHEADER') }}</p>
    <form @submit.prevent="submit">
      <pn-input-container
        :label="$t('NAME')"
        :invalid="errors.has('name')"
        :noicon="errors.has('name')"
        :valid="name.valid"
      >
        <input
          name="name"
          v-validate="'required|max:35'"
          v-model="sender.name"
          autofocus
        />
        <div v-show="errors.has('name')" class="validation-text">
          {{ $t(errors.first('name')) }}
        </div>
      </pn-input-container>

      <pn-input-container
        :label="$t('ADDRESS')"
        :invalid="errors.has('address')"
        :noicon="errors.has('address')"
        :valid="address.valid"
      >
        <input
          name="address"
          v-validate="'required|max:35'"
          v-model="sender.address"
        />
        <div v-show="errors.has('address')" class="validation-text">
          {{ $t(errors.first('address')) }}
        </div>
      </pn-input-container>

      <pn-input-container
        :label="$t('W2P_ADDRESS_OPTIONAL')"
        :invalid="errors.has('addressOptional')"
        :noicon="errors.has('addressOptional')"
        :valid="sender.addressOptional.length > 0 && addressOptional.valid"
      >
        <input
          name="addressOptional"
          v-validate="'max:35'"
          v-model="sender.addressOptional"
        />
        <div v-show="errors.has('addressOptional')" class="validation-text">
          {{ $t(errors.first('addressOptional')) }}
        </div>
      </pn-input-container>

      <country-selection
        :searchable="true"
        :value="sender.country"
        v-model="sender.country"
      ></country-selection>

      <pn-input-container
        :label="$t('W2P_LETTER_POSTALCODE')"
        :invalid="errors.has('postalCode')"
        :noicon="errors.has('postalCode')"
        :valid="postalCode.valid"
      >
        <input
          name="postalCode"
          v-validate="`required|postalCode:${sender.country}`"
          v-model="sender.postalCode"
          @blur="fetchCity"
        />
        <div v-show="errors.has('postalCode')" class="validation-text">
          {{ $t(errors.first('postalCode')) }}
        </div>
      </pn-input-container>

      <pn-input-container
        :label="$t('W2P_LETTER_CITY')"
        :invalid="errors.has('city')"
        :noicon="errors.has('city')"
        :valid="city.valid"
        :loading="loadingCity"
      >
        <input
          name="city"
          v-validate="'required|alpha_spaces|max:35'"
          v-model="sender.city"
        />
        <div v-show="errors.has('city')" class="validation-text">
          {{ $t(errors.first('city')) }}
        </div>
      </pn-input-container>

      <pn-checkbox-container
        for="saveInformation"
        :label="$t('W2P_LETTER_STORE_SENDERINFO')"
        @checkchange="checkBoxChange"
      >
        <input
          type="checkbox"
          name="saveInformation"
          v-model="sender.saveInformation"
          value="1"
        />
      </pn-checkbox-container>

      <p class="small">
        {{ $t('W2P_LETTER_ACCEPT') }}
        <a :href="getPrivacyPolicy" target="_blank">{{
          $t('W2P_LETTER_POLICY')
        }}</a>
      </p>
      <div class="button-container">
        <pn-button-container secondary size="medium">
          <button type="reset" @click="$router.push({ name: 'previewLetter' })">
            {{ $t('W2P_LETTER_BACK') }}
          </button>
        </pn-button-container>

        <pn-button-container size="medium">
          <button :disabled="isFormInvalid" type="submit" class="next">
            {{ $t('W2P_LETTER_NEXT') }}
          </button>
        </pn-button-container>
      </div>
    </form>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import { mapFields } from 'vee-validate'
import CountrySelection from '@/components/common/country/CountryComponent'
import _ from 'lodash'
import axios from 'axios'

export default {
  data: () => ({
    sender: {
      name: '',
      address: '',
      addressOptional: '',
      postalCode: '',
      city: '',
      country: 'SE',
      saveInformation: 0
    },
    loadingCity: false,
    backup: null
  }),
  components: {
    CountrySelection
  },
  mounted() {
    sessionStorage.removeItem('orderPlaced')
    if (localStorage.sender) {
      this.sender = JSON.parse(localStorage.getItem('sender'))
    } else {
      const user = JSON.parse(localStorage.getItem('userData'))
      if (user !== null) {
        this.sender.name = `${user.firstName} ${user.lastName}`
        this.sender.address = user.street
        this.sender.postalCode = user.postalCode
      }
    }

    if (Object.keys(this.senderInformation).length > 0) {
      this.sender = this.senderInformation
    }

    this.backup = Object.assign({}, this.sender)
  },
  computed: {
    ...mapState(['lang']),
    ...mapState('letter/address', ['senderInformation']),
    ...mapGetters(['getPrivacyPolicy']),
    ...mapFields([
      'name',
      'address',
      'addressOptional',
      'postalCode',
      'city',
      'saveInformation'
    ]),
    isFormInvalid() {
      return (
        this.errors.count() > 0 ||
        !Object.keys(this.fields).every(field => {
          return this.fields[field] && this.fields[field].valid
        })
      )
    }
  },
  methods: {
    ...mapMutations('letter/address', ['SAVE_SENDER']),
    ...mapMutations('letter/upload', ['SET_MATERIAL']),
    submit() {
      this.SAVE_SENDER(this.sender)

      if (!_.isEqual(this.backup, this.sender)) {
        // reset previewUrl to generate a new one
        this.SET_MATERIAL({
          key: 'previewUrl',
          value: ''
        })
      }

      this.$router.push({ name: 'recipients' })
    },
    fetchCity() {
      if (!this.postalCode.valid) {
        return
      }
      this.loadingCity = true

      axios({
        method: 'get',
        url: `${process.env.VUE_APP_SUBSCRIPTIONS_API_BASE}/search/city/${this.sender.country}/${this.sender.postalCode}`
      })
        .then(response => {
          this.sender.city = response.data.city
          this.loadingCity = false
        })
        .catch(e => {
          this.loadingCity = false
        })
    },
    checkBoxChange(e) {
      if (e.detail === true) {
        this.sender.saveInformation = 1
      } else {
        this.sender.saveInformation = 0
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.sender-page {
  pn-input-container {
    width: 100%;
    &:not(:first-child) {
      margin-top: 1em;
    }
  }

  pn-select {
    width: 100%;
    margin: 1em 0 0 0;
  }

  pn-checkbox-container {
    margin-top: 2em;
    width: 100%;
  }

  .button-container {
    margin-top: 3.5em;
    width: 100%;
    display: flex;
    justify-content: space-between;

    pn-button-container {
      width: 70%;
      max-width: 15em;
      min-width: 0;

      &:first-child {
        margin-right: 0.5em;
      }

      &:last-child {
        margin-left: 0.5em;
      }
    }
  }

  p {
    color: $gray700;
    font-weight: 300;
    margin: 1em 0em 2em;
  }

  a {
    color: $blue700;
    font-weight: 300;
  }
}
</style>
